<template>
  <div
    class="my-2 mp-ckEditor-wrapper"
    :class="{'ck-error': showError}"
  >
    <textarea
      :id="editorId"
      :name="editorId"
      :rules="rules"
      rows="10"
      cols="80"
    />

  </div>
</template>
<script>
  import { $accounts } from '@/services'
  import config from '@/components/ckEditor/ck-config.json';
  import VInput from 'vuetify/lib/components/VInput/VInput.js'
  export default {
    name: 'WysiwygEditor',
    extends: VInput,
    emits: ['update:value'],
    props: {
      editorId: {
        type: String,
        required: true,
      },
      tagPlugins: {
        type: String,
        default: () => '',
      },
      rules: {
        type: Array,
        default: () => [],
      },
      value: {
        type: String,
        required: true,
      },
      toolbar: {
        type: String,
        required: true,
      },
      defaultHeight: {
        type: Number,
        default: 200,
      },
      loader: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      dynamicContentData: {
        id: '',
        name: '',
        editor: '',
        contentFeedId: '',
        content_parts: [],
      },
      dataLoading: false,
      editorInstance: null,
    }),
    computed: {
      showError () {
        return this.shouldValidate && this.errorBucket.length
      },
    },
    watch: {
      value (newVal, oldVal) {
        if (CKEDITOR.instances[this.editorId]) {
          const editorData = CKEDITOR.instances[this.editorId].getData()
          if (newVal !== oldVal && newVal !== editorData) {
            CKEDITOR.instances[this.editorId].setData(newVal)
          }
        }
      },
    },
    async mounted () {
      // load all tag plugins
      const plugins = [
        ['campaigntags', 'Campaign Tags', $accounts.getCampaignTags],
        ['dynamictags', 'Dynamic Areas', $accounts.getDynamicAreaTags],
        ['contacttags', 'Contact Tags', $accounts.getContactTags],
        ['coupontags', 'Coupon Tags', $accounts.getCouponTags],
        ['tabletags', 'Table Tags', $accounts.getTableTags],
        ['othertags', 'Other Tags', $accounts.getOtherTags],
        ['funneltags', 'Web Funnel Tags', $accounts.getFunnelTags],
        ['abandonedcarttags', 'Abandoned Cart Tags', $accounts.getAbandonedCartTags],
        ['productfeedtemplatestags', 'Product Feed Templates Tags', $accounts.getProductFeedTemplatesTags],
        ['dynamiccontenttags', 'Dynamic Content Tags', $accounts.getDynamicContentTags],
        ['templatetags', 'Template Tags', $accounts.getTemplateTags],
        ['optintag', 'Optin Tags', $accounts.getOptinTags],
        ['preferencetags', 'Preference Tags', $accounts.getPreferenceTags],
      ]
      const requests = []
      
      const requiredPlugins = plugins.filter(p => this.tagPlugins.includes(p[0]))
      requiredPlugins.forEach(plugin => {
        requests.push(this.loadTagPlugin(...plugin))
      })
      await Promise.all(requests)
      this.asyncSavePlugin()

      // CkEditor
      config.toolbar = this.toolbar
      CKEDITOR.config.height = this.defaultHeight
      CKEDITOR.plugins.addExternal('codemirror', '/codemirror/', 'plugin.js')

      config.extraPlugins = `AsyncSave, codemirror,docprops, ${this.tagPlugins}`
      CKEDITOR.replace(this.editorId, config)
      // Set data
      this.value && CKEDITOR.instances[this.editorId].setData(this.value)
      // On Change update value
      CKEDITOR.instances[this.editorId].on('change', (evt) => {
        this.updateContent()
      })
      /**
       * On instance ready state.
       * Supplied function prop as onEditorReady to the editor component and being called once instance is ready.
       */
      CKEDITOR.instances[this.editorId].on('instanceReady', (evt) => {
        this.$emit('onEditorReady')
      })
      CKEDITOR.instances[this.editorId].on('triggerContentDialog', (evt) => {
        this.$refs.contentDialog.openDialog()
      })
    },
    beforeDestroy() {
      if (CKEDITOR.instances[this.editorId]) {
        CKEDITOR.instances[this.editorId].destroy(true);
      }
    },
    methods: {
      /**
       * update value props
       */
      updateContent(){
        const editorData = CKEDITOR.instances[this.editorId].getData()
        this.$emit('update:value', editorData)
        // Following command will execute on content update in editor and call parent method.
        this.$emit('onContentUpdate', editorData)
      }, 
      /**
       * get custom tag from api and load tag plugin
       * @param {String} name
       * @param {String} label
       * @param {Function} tagFunc
       */
      async loadTagPlugin (name, label, tagFunc) {
        if (Object.keys(CKEDITOR.plugins.registered).includes(name)) {
          return ;
        }
        let PluginData = []
        try {
          const response = await tagFunc()
          if (response.data && response.data.data) {
            PluginData = response.data.data
          }
        } catch (err) {
          this.$store.commit('ui/snackbar', {
            message: `Unable to load ${name} data`,
            type: 'error',
          })
        }
        this.createTagPlugin(name, label, PluginData)
      },
      /**
       * Create new tag plugin
       * @param {String} name
       * @param {String} label
       * @param {Object} data
       */
      createTagPlugin (name, label, data) {
        if (Object.keys(CKEDITOR.plugins.registered).includes(name)) {
          // Plugin already exist
          return
        }

        // Create plugin
        CKEDITOR.plugins.add(name, {
          requires: ['richcombo'],
          init: function (editor) {
            editor.ui.addRichCombo(name, {
              label: label,
              title: label,
              voiceLabel: label,
              className: 'cke_tag_plugin',
              multiSelect: false,
              panel: {
                css: [...editor.config.contentsCss, CKEDITOR.skin.getPath('editor')],
                voiceLabel: editor.lang.panelVoiceLabel,
              },
              init: function () {
                this.startGroup(label)
                data.forEach(da => {
                  this.add(da, da, undefined)
                })
              },
              onClick: function (value) {
                editor.focus()
                editor.insertHtml(value)
                editor.fire('saveSnapshot')
              },
            })
          },
        })
      },
      /**
       * Asynchronous save plugin
       * trigger submitform event on click
       */
      asyncSavePlugin () {
        const name = 'AsyncSave'
        if (Object.keys(CKEDITOR.plugins.registered).includes(name)) {
          // Plugin already exist
          return
        }

        // Create plugin
        CKEDITOR.plugins.add(name, {
          init: function (editor) {
            editor.addCommand(name, {
              exec: (editor) => {
                /**
                 * Ckeditor will look for save button using ck-editor-save class
                 * and trigger it.
                 * Note: Add ck-editor-save class to your create/update component save button,
                 */
                const btn = document.querySelector('.ck-editor-save')
                btn.click()
              },
            })
            editor.ui.addButton('asyncsave', {
              label: editor.lang.save.toolbar,
              command: name,
              toolbar: 'document,10',
              icon: require('@/assets/ck_save.png'),
            })
          },
        })
      },
    },
  }
</script>
<style lang="scss">
body {
  /* We need to assaign this CSS Custom property to the body instead of :root, because of CSS Specificity and codepen stylesheet placement before loaded CKE5 content. */
  --ck-z-default: 3100;
  --ck-z-modal: calc( var(--ck-z-default) + 3999 );
}
.mp-ckEditor-wrapper {
  .ck-error .cke_chrome {
    border-color: #b30a28;
  }
  .cke_tag_plugin a span.cke_combo_text {
    width: 90px;
  }
  .cke_toolgroup{
    border: 1px solid #ddd !important;
    margin-right: 2px !important;
    padding-right: 0px !important;
  }
  a.cke_combo_button{
    border: 1px solid #ddd !important;
    margin: 0 4px 0px 0 !important;
    padding:0px !important;
  }
  .cke_combo::after{
    border-right: 0px !important;
  }
  .cke_toolgroup a.cke_button:last-child::after{
    border-right: 0px !important;
  }
  .cke_panel.cke_combopanel{
    min-width: 240px !important
  }
  .cke_toolbar_last {
    display: flex !important;
    flex-wrap: wrap !important;
  }
}
.cke_panel.cke_combopanel{
  width: 260px !important
}
.add-dynamic-content-button .cke_button_label{
  display: inline-block !important;
}
.add-dynamic-content-button .cke_button_icon{
  display: none !important;
}
.ck-dialog.v-dialog.v-dialog--active.v-dialog--persistent{
  box-shadow: 0px 0px 10px 0px #ddd !important;
}
</style>
